import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { Simulate } from 'react-dom/test-utils';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { dropdownDriverFactory as coreDriverFactory } from '../internal/WixUiCoreComponents/components/CoreDropdown/CoreDropdown.driver';

import { hasDataAttr, hasMobile } from '../../testkitUtils/utils';
import { DATA_ATTRIBUTES, DATA_HOOKS } from './constants';
import {
  IconTooltipDriver,
  iconTooltipDriverFactory,
} from '../IconTooltip/IconTooltip.driver';
import { KeyDefinitionType } from '@wix/unidriver-core';
import { DropdownContentOptionDriver } from '../internal/WixUiCoreComponents/components/CoreDropdownOption/CoreDropdownOption.driver';

export interface DropdownDriver extends BaseDropdownDriver, BaseUniDriver {
  isMobile(): Promise<boolean>;
  isNativeSelect(): Promise<boolean>;
  getDropdownCoreDriver(baseUniDriver: BaseUniDriver);
  isErrorTooltipShown(): Promise<boolean>;
  clickOnErrorIcon(): Promise<void>;
  getOptionAt(index: number): Promise<DropdownContentOptionDriver>;
}

interface BaseDropdownDriver {
  errorIcon: IconTooltipDriver;
  getLabelText(): Promise<string>;
  isDisabled(): Promise<boolean>;
  isFocused(): Promise<boolean>;
  areOptionsShown(): Promise<boolean>;
  getOptionsCount(): Promise<number>;
  selectOptionAt(index: number): Promise<void>;
  hoverOptionAt(index: number): Promise<void>;
  hasError(): Promise<boolean>;
  hasErrorMessage(): Promise<boolean>;
  getErrorMessageContent(): Promise<string>;
  click(): Promise<void>;
  getAriaActivedescendant(): Promise<string | null>;
  getDropdownId(): Promise<string | null>;
  getLabelForInput(): Promise<string | null>;
  getAriaExpanded(): Promise<string | null>;
  getAriaLabel(): Promise<string | null>;
  getAriaLabelledBy(): Promise<string | null>;
  getAriaDescribedBy(): Promise<string | null>;
  getAriaInvalid(): Promise<string | null>;
  getDropdownText(): Promise<string>;
  getLang(): Promise<string>;
  getSuffixAt(index: number): Promise<BaseUniDriver>;
  getIsLoading(): Promise<boolean>;
  getLabelAsterisk(): Promise<UniDriver>;
  keydown: (key: KeyDefinitionType) => Promise<void>;
}

const getDropdownCoreDriver = async (baseUniDriver: BaseUniDriver) => {
  const element = (await baseUniDriver.element()).querySelector(
    `[data-hook="${DATA_HOOKS.coreDropdown}"]`,
  );
  return coreDriverFactory({ element, eventTrigger: Simulate });
};

const getDropdownNativeSelect = async (base: UniDriver) => {
  return base.$(`[data-hook="${DATA_HOOKS.nativeSelect}"]`);
};

const getSpinnerDriver = (base: UniDriver) => {
  return base.$(`[data-hook="${DATA_HOOKS.loader}"]`);
};

const regularDriver = (
  base: UniDriver,
  baseUniDriver: BaseUniDriver,
  body: UniDriver,
): BaseDropdownDriver => {
  const getDropdownBase = async () => {
    return base.$(`[data-hook="${DATA_HOOKS.base}"]`);
  };
  const getLabel = async () => {
    return base.$(`[data-hook="${DATA_HOOKS.label}"]`);
  };
  function getIconTooltipDriver() {
    return iconTooltipDriverFactory(
      base.$(`[data-hook="${DATA_HOOKS.errorTooltip}"]`),
      body,
    );
  }
  return {
    errorIcon: getIconTooltipDriver(),
    getLabelText: async () => (await getLabel()).text(),
    getLabelAsterisk: async () =>
      base.$(`[data-hook="${DATA_HOOKS.labelAsterisk}"]`),
    isDisabled: async () =>
      (await (await getDropdownBase()).attr('disabled')) !== null,
    isFocused: async () =>
      (await (await getDropdownBase()).attr('data-hook')) ===
      document.activeElement.getAttribute('data-hook'),
    getAriaActivedescendant: async () =>
      (await getDropdownBase()).attr('aria-activedescendant'),
    getAriaExpanded: async () =>
      (await getDropdownBase()).attr('aria-expanded'),
    getLabelForInput: async () => (await getLabel()).attr('for'),
    getDropdownId: async () => (await getDropdownBase()).attr('id'),
    getAriaLabel: async () => (await getDropdownBase()).attr('aria-label'),
    getAriaLabelledBy: async () =>
      (await getDropdownBase()).attr('aria-labelledby'),
    getAriaDescribedBy: async () =>
      (await getDropdownBase()).attr('aria-describedby'),
    getAriaInvalid: async () => (await getDropdownBase()).attr('aria-invalid'),
    getLang: async () => base.attr('lang'),
    click: async () => (await getDropdownCoreDriver(baseUniDriver)).click(),
    areOptionsShown: async () =>
      (await getDropdownCoreDriver(baseUniDriver)).isContentElementExists(),
    getOptionsCount: async () =>
      (await getDropdownCoreDriver(baseUniDriver)).getOptionsCount(),
    selectOptionAt: async (index: number) => {
      (await getDropdownCoreDriver(baseUniDriver)).optionAt(index).click();
    },
    hoverOptionAt: async (index: number) => {
      (await getDropdownCoreDriver(baseUniDriver)).optionAt(index).mouseEnter();
    },
    getDropdownText: async () =>
      base.$(`[data-hook="${DATA_HOOKS.baseText}"]`).text(),
    hasErrorMessage: async () => getIconTooltipDriver().exists(),
    hasError: async () =>
      hasDataAttr(await getDropdownBase(), 'dropdown-base-error', 'true'),
    getErrorMessageContent: async () => {
      const ariaDescribedByArr = (
        await (await getDropdownBase()).attr('aria-describedby')
      ).split(' ');
      const errorAriaDescribedBy = ariaDescribedByArr.find(
        (val) => val.indexOf('error-message') > -1,
      );

      const tooltipContent = document.querySelector(`#${errorAriaDescribedBy}`);
      return tooltipContent ? tooltipContent.textContent : '';
    },
    getSuffixAt: async (index: number) => {
      const dropdownOptions = base.$$(`[data-hook="${DATA_HOOKS.option}"]`);
      return baseUniDriverFactory(dropdownOptions.get(index));
    },
    getIsLoading: async () => {
      const spinnerDriver = getSpinnerDriver(base);
      return spinnerDriver.exists();
    },
    keydown: async (key: KeyDefinitionType) => {
      await base.$(`[data-hook="${DATA_HOOKS.base}"]`).pressKey(key);
    },
  };
};

const nativeDriver = (
  base: UniDriver,
  _: BaseUniDriver,
  body: UniDriver,
): BaseDropdownDriver => {
  const getLabel = async () => {
    return base.$(`[data-hook="${DATA_HOOKS.label}"]`);
  };
  const getNativeOptions = () =>
    base.$$(`option:not([data-hook="${DATA_HOOKS.placeholderOption}"])`);
  const warnUnsupportedFunction = (fnName: string) => {
    console.warn(
      `Function ${fnName} is not supported within native dropdown mode.`,
    );
    return null;
  };
  function getIconTooltipDriver() {
    return iconTooltipDriverFactory(
      base.$(`[data-hook="${DATA_HOOKS.errorTooltip}"]`),
      body,
    );
  }

  return {
    errorIcon: getIconTooltipDriver(),
    getLabelText: async () => (await getLabel()).text(),
    getLabelAsterisk: async () =>
      base.$(`[data-hook="${DATA_HOOKS.labelAsterisk}"]`),
    isDisabled: async () =>
      (await (await getDropdownNativeSelect(base)).attr('disabled')) !== null,
    isFocused: async () =>
      (await (await getDropdownNativeSelect(base)).attr('data-hook')) ===
      document.activeElement.getAttribute('data-hook'),
    getAriaLabel: async () =>
      (await getDropdownNativeSelect(base)).attr('aria-label'),
    getAriaLabelledBy: async () =>
      (await getDropdownNativeSelect(base)).attr('aria-labelledby'),
    getAriaDescribedBy: async () =>
      (await getDropdownNativeSelect(base)).attr('aria-describedby'),
    getDropdownId: async () => (await getDropdownNativeSelect(base)).attr('id'),
    getAriaActivedescendant: async () =>
      warnUnsupportedFunction('getAriaActivedescendant'),
    getLabelForInput: async () => (await getLabel()).attr('for'),
    getAriaExpanded: async () => warnUnsupportedFunction('getAriaExpanded'),
    getAriaInvalid: async () =>
      (await getDropdownNativeSelect(base)).attr('aria-invalid'),
    getLang: async () => base.attr('lang'),
    click: async () => warnUnsupportedFunction('click'),
    areOptionsShown: async () => warnUnsupportedFunction('areOptionsShown'),
    selectOptionAt: async (index: number) => {
      const option = getNativeOptions().get(index);
      const isDisabled = (await option.attr('disabled')) !== null;
      !isDisabled && Simulate.change(await option.getNative());
    },
    hoverOptionAt: async (_index) => warnUnsupportedFunction('hoverOptionAt'),
    getOptionsCount: async () => getNativeOptions().count(),
    hasErrorMessage: async () => getIconTooltipDriver().exists(),
    hasError: async () =>
      (await (
        await getDropdownNativeSelect(base)
      ).attr(DATA_ATTRIBUTES.error)) !== null,
    getErrorMessageContent: async () => {
      return getIconTooltipDriver().getTooltipContent().text();
    },
    getDropdownText: async () =>
      (await (await getDropdownNativeSelect(base))._prop('selectedOptions'))[0]
        .text,
    getSuffixAt: () => {
      throw new Error('not supported on mobile');
    },
    getIsLoading: async () => {
      const spinnerDriver = getSpinnerDriver(base);
      return spinnerDriver.exists();
    },
    keydown: async (key: KeyDefinitionType) => {
      await base.pressKey(key);
    },
  };
};

export const dropdownDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): DropdownDriver => {
  const baseUniDriver = baseUniDriverFactory(base);
  const isNativeSelect = async () =>
    (await getDropdownNativeSelect(base)).exists();
  const getDriver = async () =>
    (await isNativeSelect())
      ? nativeDriver(base, baseUniDriver, body)
      : regularDriver(base, baseUniDriver, body);
  function getIconTooltipDriver() {
    return iconTooltipDriverFactory(
      base.$(`[data-hook="${DATA_HOOKS.errorTooltip}"]`),
      body,
    );
  }
  return {
    ...baseUniDriver,
    errorIcon: getIconTooltipDriver(),
    isMobile: () => hasMobile(base),
    isNativeSelect,
    isDisabled: async () => (await getDriver()).isDisabled(),
    isFocused: async () => (await getDriver()).isFocused(),
    getAriaActivedescendant: async () =>
      (await getDriver()).getAriaActivedescendant(),
    getAriaExpanded: async () => (await getDriver()).getAriaExpanded(),
    getDropdownId: async () => (await getDriver()).getDropdownId(),
    getLabelText: async () => (await getDriver()).getLabelText(),
    getLabelForInput: async () => (await getDriver()).getLabelForInput(),
    getAriaLabel: async () => (await getDriver()).getAriaLabel(),
    getAriaLabelledBy: async () => (await getDriver()).getAriaLabelledBy(),
    getAriaDescribedBy: async () => (await getDriver()).getAriaDescribedBy(),
    getAriaInvalid: async () => (await getDriver()).getAriaInvalid(),
    getLang: async () => (await getDriver()).getLang(),
    click: async () => (await getDriver()).click(),
    areOptionsShown: async () => (await getDriver()).areOptionsShown(),
    getOptionsCount: async () => (await getDriver()).getOptionsCount(),
    getOptionAt: async (index: number) => {
      return (await getDropdownCoreDriver(baseUniDriver)).optionAt(index);
    },
    selectOptionAt: async (index: number) =>
      (await getDriver()).selectOptionAt(index),
    hoverOptionAt: async (index: number) =>
      (await getDriver()).hoverOptionAt(index),
    hasErrorMessage: async () => (await getDriver()).hasErrorMessage(),
    hasError: async () => (await getDriver()).hasError(),
    getErrorMessageContent: async () =>
      (await getDriver()).getErrorMessageContent(),
    getDropdownText: async () => (await getDriver()).getDropdownText(),
    getDropdownCoreDriver,
    async isErrorTooltipShown() {
      const iconTooltipDriver = getIconTooltipDriver();
      return iconTooltipDriver.tooltipExists();
    },
    async clickOnErrorIcon() {
      const iconTooltipDriver = getIconTooltipDriver();
      await iconTooltipDriver.click();
    },
    getLabelAsterisk: async () => (await getDriver()).getLabelAsterisk(),
    getSuffixAt: async (index: number) =>
      (await getDriver()).getSuffixAt(index),
    getIsLoading: async () => {
      const spinnerDriver = getSpinnerDriver(base);
      return spinnerDriver.exists();
    },
    keydown: async (key: KeyDefinitionType) => {
      (await getDriver()).keydown(key);
    },
  };
};
